import { useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { getLocation, type LocationDetail } from '../locations/api';
import { LocationPicker } from '../locations/LocationPicker';
import {
  knownLocations, POSTABLE_TYPES, type MovementInput, type MovementType,
  type Placement, type Posting, type TransferInput,
} from './api';
import {
  EMPTY_OPERATION, normalizeQuantity, operationFailure, readPendingOperation,
  storePendingOperation, type StockOperationDraft,
} from './stockOperation';

interface Props {
  componentId: string;
  componentName: string;
  unit?: string | null;
  places: Placement[];
  onPost: (input: MovementInput) => Promise<Posting>;
  onTransfer: (input: TransferInput) => Promise<Posting>;
  onReview: () => Promise<void>;
}

type Feedback =
  | { kind: 'success'; draft: StockOperationDraft; reviewed: boolean }
  | { kind: 'failure'; code: string; uncertain: boolean };

export function StockOperationForm({
  componentId, componentName, unit, places, onPost, onTransfer, onReview,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fieldId = useId();
  const storageKey = `storageapp.stock.pending.${user?.id}.${componentId}`;
  const [restored] = useState(() => readPendingOperation(storageKey));
  const [draft, setDraft] = useState<StockOperationDraft>(restored ?? EMPTY_OPERATION);
  const [feedback, setFeedback] = useState<Feedback | null>(
    restored ? { kind: 'failure', code: 'unknown', uncertain: true } : null,
  );
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const [validation, setValidation] = useState<string | null>(null);
  const [notice, setNotice] = useState(false);
  const [scanInto, setScanInto] = useState<'from' | 'to' | null>(null);
  const uncertain = feedback?.kind === 'failure' && feedback.uncertain;
  const locked = busy || uncertain;

  const known = useQuery({
    queryKey: ['stock', 'known-locations', componentId],
    queryFn: () => knownLocations(componentId),
  });
  const source = useQuery({
    queryKey: ['locations', 'detail', draft.from],
    queryFn: () => getLocation(draft.from!),
    enabled: draft.mode === 'transfer' && Boolean(draft.from),
  });
  const destination = useQuery({
    queryKey: ['locations', 'detail', draft.to],
    queryFn: () => getLocation(draft.to!),
    enabled: Boolean(draft.to),
  });

  const placeLabel = (id: string | null, detail?: LocationDetail): string | null => {
    if (!id) return t('stock.outside');
    const place = [...places, ...(known.data ?? [])].find((row) => row.location.id === id);
    if (place) return [...place.ancestors, `${place.location.name} (${place.location.code})`].join(' → ');
    if (detail?.id !== id) return null;
    return [...detail.breadcrumbs.filter((row) => row.id !== id).map((row) => row.name), `${detail.name} (${detail.code})`].join(' → ');
  };
  const sourceLabel = placeLabel(draft.from, source.data);
  const destinationLabel = placeLabel(draft.to, destination.data);
  const placesReady = Boolean(destinationLabel && (draft.mode !== 'transfer' || sourceLabel));
  const operationName = (operation: StockOperationDraft) => t(
    operation.mode === 'transfer' ? 'stock.transferMode' : `stock.type.${operation.type}`,
  );
  const update = (patch: Partial<StockOperationDraft>) => {
    if (locked) return;
    setDraft((current) => ({ ...current, ...patch }));
    setValidation(null);
    setNotice(false);
  };
  const finish = (reviewed: boolean) => {
    storePendingOperation(storageKey, null);
    setFeedback({ kind: 'success', draft, reviewed });
    setDraft((current) => ({ ...current, quantity: '', note: '' }));
    setScanInto(null);
    setChecked(false);
    setNotice(false);
  };

  const submit = async () => {
    if (inFlight.current || locked) return;
    const quantity = normalizeQuantity(draft.quantity);
    if (!quantity) { setValidation('quantity'); return; }
    if (!placesReady) { setValidation('place'); return; }
    if (draft.mode === 'transfer' && draft.from === draft.to) {
      setValidation('samePlace');
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setFeedback(null);
    setNotice(false);
    setValidation(null);
    setChecked(false);
    setCheckFailed(false);
    storePendingOperation(storageKey, draft);
    try {
      if (draft.mode === 'transfer') {
        await onTransfer({
          component_id: componentId, from_location_id: draft.from, to_location_id: draft.to,
          quantity, note: draft.note || null,
        });
      } else {
        await onPost({
          component_id: componentId, location_id: draft.to, movement_type: draft.type,
          quantity, note: draft.note || null,
        });
      }
      finish(false);
    } catch (error) {
      const failure = operationFailure(error);
      if (!failure.uncertain) storePendingOperation(storageKey, null);
      setFeedback({ kind: 'failure', ...failure });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const review = async () => {
    if (checking) return;
    setChecking(true);
    setChecked(false);
    setCheckFailed(false);
    try {
      await onReview();
      setChecked(true);
    } catch {
      setCheckFailed(true);
    } finally {
      setChecking(false);
    }
  };

  const outgoing = draft.type === 'issue' || draft.type === 'write_off';
  const fromLabel = draft.mode === 'transfer' ? sourceLabel : outgoing ? destinationLabel : t('stock.outside');
  const toLabel = draft.mode === 'transfer' ? destinationLabel : outgoing ? t('stock.outside') : destinationLabel;

  return (
    <form className="stock__form" aria-label={t('stock.operation.title')} aria-busy={busy}
      onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <h4>{t('stock.operation.title')}</h4>
      <fieldset disabled={locked} className="stock__operation-fields">
        <legend className="sr-only">{t('stock.operation.title')}</legend>
        <div className="stock__modes">
          <button type="button" aria-pressed={draft.mode === 'post'} className={draft.mode === 'post' ? 'is-active' : undefined}
            onClick={() => { update({ mode: 'post' }); setScanInto(null); }}>{t('stock.postMode')}</button>
          <button type="button" aria-pressed={draft.mode === 'transfer'} className={draft.mode === 'transfer' ? 'is-active' : undefined}
            onClick={() => { update({ mode: 'transfer' }); setScanInto(null); }}>{t('stock.transferMode')}</button>
        </div>
        {draft.mode === 'post' ? (
          <label className="stock__operation-field">
            {t('stock.operation.kind')}
            <select value={draft.type} onChange={(event) => update({ type: event.target.value as MovementType })}>
              {POSTABLE_TYPES.map((type) => <option key={type} value={type}>{t(`stock.type.${type}`)}</option>)}
            </select>
          </label>
        ) : (
          <div className="stock__operation-field"><span>{t('stock.from')}</span>
            <LocationPicker label={t('stock.from')} value={draft.from} componentId={componentId}
              onChange={(from) => update({ from })} scanTarget={!locked && scanInto === 'from'} onFocus={() => setScanInto('from')} />
          </div>
        )}
        <div className="stock__operation-field"><span>{draft.mode === 'transfer' ? t('stock.to') : t('stock.place')}</span>
          <LocationPicker label={draft.mode === 'transfer' ? t('stock.to') : t('stock.place')}
            value={draft.to} componentId={componentId} onChange={(to) => update({ to })}
            scanTarget={!locked && scanInto === 'to'} onFocus={() => setScanInto('to')} />
        </div>
        <label className="stock__operation-field" htmlFor={`${fieldId}-quantity`}>
          {t('stock.quantity')} {unit}
          <input id={`${fieldId}-quantity`} value={draft.quantity} onChange={(event) => update({ quantity: event.target.value })}
            placeholder={t('stock.quantity')} inputMode="decimal" required aria-invalid={validation === 'quantity'}
            aria-describedby={validation ? `${fieldId}-validation` : undefined} />
        </label>
        <label className="stock__operation-field stock__operation-note" htmlFor={`${fieldId}-note`}>
          {t('stock.note')}
          <input id={`${fieldId}-note`} value={draft.note} onChange={(event) => update({ note: event.target.value })}
            placeholder={t('stock.note')} maxLength={2000} />
        </label>
      </fieldset>

      <dl className="stock__operation-summary" aria-label={t('stock.operation.summary')}>
        <div><dt>{t('stock.operation.component')}</dt><dd>{componentName}</dd></div>
        <div><dt>{t('stock.operation.kind')}</dt><dd>{operationName(draft)}</dd></div>
        <div><dt>{t('stock.quantity')}</dt><dd>{normalizeQuantity(draft.quantity) ? `${num(normalizeQuantity(draft.quantity)!)} ${unit ?? ''}` : t('stock.operation.enterQuantity')}</dd></div>
        {draft.mode === 'post' && draft.type === 'adjustment' ? (
          <div><dt>{t('stock.place')}</dt><dd>{destinationLabel ?? t('common.loading')}</dd></div>
        ) : (
          <><div><dt>{t('stock.from')}</dt><dd>{fromLabel ?? t('common.loading')}</dd></div>
            <div><dt>{t('stock.to')}</dt><dd>{toLabel ?? t('common.loading')}</dd></div></>
        )}
      </dl>

      {validation && <p className="panel__error" id={`${fieldId}-validation`} role="alert">{t(`stock.operation.validation.${validation}`)}</p>}
      {!placesReady && (source.isError || destination.isError) && (
        <div className="panel__error" role="alert"><p>{t('stock.operation.validation.place')}</p>
          <button type="button" disabled={locked} onClick={() => {
            if (draft.mode === 'transfer' && draft.from) void source.refetch();
            if (draft.to) void destination.refetch();
          }}>{t('stock.operation.retryPlace')}</button>
        </div>
      )}
      {busy && <p role="status">{t('stock.operation.pending')}</p>}
      {feedback?.kind === 'success' && (
        <p className="stock__operation-success" role="status">{t(feedback.reviewed ? 'stock.operation.verified' : 'stock.operation.success', {
          operation: operationName(feedback.draft), quantity: num(normalizeQuantity(feedback.draft.quantity)!), unit: unit ?? '',
        })}</p>
      )}
      {feedback?.kind === 'failure' && !feedback.uncertain && (
        <div className="panel__error stock__operation-feedback" role="alert">
          <p>{t('stock.operation.rejected')} {t(`stock.errors.${feedback.code}`, t('stock.errors.unknown'))}</p>
          <p>{t('stock.operation.inputsKept')}</p>
        </div>
      )}
      {uncertain && (
        <div className="stock__warning stock__operation-feedback" role="alert">
          <p>{t('stock.operation.uncertain')}</p>
          <button type="button" onClick={() => void review()} disabled={checking}>{t(checking ? 'stock.operation.checking' : 'stock.operation.check')}</button>
          {checkFailed && <p>{t('stock.operation.checkFailed')}</p>}
          {checked && <>
            <p>{t('stock.operation.checked')}</p>
            <div className="stock__operation-review">
              <button type="button" onClick={() => finish(true)}>{t('stock.operation.found')}</button>
              <button type="button" onClick={() => {
                storePendingOperation(storageKey, null);
                setFeedback(null);
                setChecked(false);
                setNotice(true);
                setScanInto(null);
              }}>{t('stock.operation.notFound')}</button>
            </div>
          </>}
        </div>
      )}
      {notice && <p role="status">{t('stock.operation.readyAfterCheck')}</p>}
      <button type="submit" className="primary" disabled={locked || !placesReady}>
        {t(busy ? 'stock.operation.pending' : 'stock.post')}
      </button>
    </form>
  );
}
